import { supabase } from './supabaseClient';

export type InGameBbgBalance = {
  symbol: 'BBG';
  name: string;
  balance: string;
  available: string;
  reserved: string;
  decimals: number;
};

type InGameWalletBalanceRow = {
  token_config_id: string;
  symbol: string;
  name: string;
  decimals: number;
  balance_base_units: string;
  reserved_base_units: string;
  available_base_units: string;
};

function formatBaseUnits(baseUnits: string, decimals: number): string {
  const normalized = String(baseUnits || '0').split('.')[0] || '0';
  const formatThousands = (value: string) => value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (decimals <= 0) {
    return formatThousands(normalized.replace(/^0+(?=\d)/, '') || '0');
  }

  const padded = normalized.padStart(decimals + 1, '0');
  const integerPart = padded.slice(0, -decimals).replace(/^0+(?=\d)/, '') || '0';
  const fractionPart = padded.slice(-decimals).slice(0, 2).padEnd(2, '0');
  return `${formatThousands(integerPart)}.${fractionPart}`;
}

export async function getInGameBbgBalance(userId: string): Promise<InGameBbgBalance | null> {
  if (!supabase) {
    throw new Error('Supabase 尚未設定，無法查詢 BBG 遊戲內餘額。');
  }

  const { data: walletBalanceResult, error: walletError } = await supabase
    .schema('api')
    .rpc('get_my_in_game_wallet_balances_v1');

  if (walletError) throw walletError;

  // Cross-schema RPC inference may describe the response as a single object.
  // The SQL contract is RETURNS TABLE, so normalize the runtime response before
  // using array methods and keep the callback explicitly typed for tsc.
  const walletBalances: InGameWalletBalanceRow[] = Array.isArray(walletBalanceResult)
    ? walletBalanceResult as InGameWalletBalanceRow[]
    : [];

  // The API contract authorizes the current session with auth.uid(). Keep the
  // existing argument for caller compatibility while avoiding client-side
  // user-id filtering as an authorization mechanism.
  void userId;
  const wallet = walletBalances.find((balance: InGameWalletBalanceRow) => balance.symbol === 'BBG');
  if (!wallet) return null;

  return {
    symbol: 'BBG',
    name: wallet.name,
    balance: formatBaseUnits(wallet.balance_base_units, wallet.decimals),
    available: formatBaseUnits(wallet.available_base_units, wallet.decimals),
    reserved: formatBaseUnits(wallet.reserved_base_units, wallet.decimals),
    decimals: wallet.decimals,
  };
}
